import { useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { Unarchive as RestoreIcon } from "@mui/icons-material";
import type { PlaybookSummary } from "../../services/lensService";

const useStyles = makeStyles()((theme) => ({
    toolbar: { marginBottom: theme.spacing(4) },
    empty: { padding: theme.spacing(6), textAlign: "center", color: "#999" },
    center: { display: "flex", justifyContent: "center", padding: theme.spacing(6) },
    row: { cursor: "pointer" },
    archivedRow: { backgroundColor: "#fafafa" },
}));

type Filter = "active" | "archived" | "all";

const rowsPerPageOptions = [10, 25, 50, 100];

// "Live" only when a review would actually find something: enabled with at least one approved provision.
const statusOf = (playbook: PlaybookSummary): { label: string; colour: "default" | "success" | "warning"; title: string } => {
    if (playbook.archived) return { label: "Archived", colour: "default", title: "Hidden and not reviewable. Nothing is deleted." };
    if (!playbook.enabled) return { label: "Disabled", colour: "default", title: "Not offered for review until it is enabled." };
    if (playbook.provisionCount === 0)
        return { label: "Not reviewable", colour: "warning", title: "No approved provisions, so a review would find nothing." };
    return { label: "Live", colour: "success", title: `Reviewed against ${playbook.provisionCount} approved provisions.` };
};

interface PlaybookTableProps {
    playbooks: PlaybookSummary[];
    isLoading: boolean;
    isError: boolean;
    onOpen: (key: string) => void;
    onRestore: (key: string) => void;
    restoringKey: string | null;
}

const PlaybookTable = ({ playbooks, isLoading, isError, onOpen, onRestore, restoringKey }: PlaybookTableProps) => {
    const { classes } = useStyles();
    const [filter, setFilter] = useState<Filter>("active");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const archivedCount = playbooks.filter((p) => p.archived).length;
    const rows = filter === "all" ? playbooks : playbooks.filter((p) => p.archived === (filter === "archived"));
    // Clamped, as a restore or refetch can shrink the list under the current page.
    const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, lastPage);
    const pageRows = rows.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

    return (
        <>
            <Box className={classes.toolbar}>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={filter}
                    onChange={(_e, val: Filter | null) => {
                        if (!val) return;
                        setFilter(val);
                        setPage(0);
                    }}
                >
                    <ToggleButton value="active">Active ({playbooks.length - archivedCount})</ToggleButton>
                    <ToggleButton value="archived">Archived ({archivedCount})</ToggleButton>
                    <ToggleButton value="all">All ({playbooks.length})</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Paper>
                {isLoading ? (
                    <Box className={classes.center}>
                        <CircularProgress />
                    </Box>
                ) : rows.length === 0 ? (
                    <Box className={classes.empty}>
                        <Typography variant="body1">
                            {isError
                                ? "Could not load the playbooks."
                                : playbooks.length === 0
                                  ? "No playbooks yet."
                                  : "No playbooks match this filter."}
                        </Typography>
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ fontWeight: 600 }}>Name</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Version</TableCell>
                                <TableCell style={{ fontWeight: 600 }} align="right">Approved</TableCell>
                                <TableCell style={{ fontWeight: 600 }} align="right">Drafts</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pageRows.map((playbook) => {
                                const status = statusOf(playbook);
                                return (
                                    <TableRow
                                        key={playbook.key}
                                        hover={!playbook.archived}
                                        className={playbook.archived ? classes.archivedRow : classes.row}
                                        onClick={playbook.archived ? undefined : () => onOpen(playbook.key)}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                {playbook.displayName || playbook.key}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: "#777" }}>
                                                {playbook.key}
                                            </Typography>
                                        </TableCell>
                                        <TableCell style={{ color: "#555" }}>v{playbook.version}</TableCell>
                                        <TableCell align="right">{playbook.provisionCount}</TableCell>
                                        <TableCell align="right">{playbook.draftCount || "—"}</TableCell>
                                        <TableCell>
                                            <Tooltip title={status.title}>
                                                <Chip size="small" variant="outlined" color={status.colour} label={status.label} />
                                            </Tooltip>
                                            {playbook.generationStatus === "generating" && (
                                                <Tooltip title={playbook.generationProgress ?? "Drafting with AI"}>
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        icon={<CircularProgress size={12} />}
                                                        label="Generating…"
                                                        style={{ marginLeft: 8 }}
                                                    />
                                                </Tooltip>
                                            )}
                                            {playbook.generationStatus === "failed" && (
                                                <Tooltip title={playbook.generationError ?? "Generation stopped before it finished."}>
                                                    <Chip size="small" variant="outlined" color="error" label="Failed" style={{ marginLeft: 8 }} />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {playbook.archived && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<RestoreIcon fontSize="small" />}
                                                    disabled={restoringKey === playbook.key}
                                                    onClick={() => onRestore(playbook.key)}
                                                >
                                                    Restore
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={rowsPerPageOptions}
                                    count={rows.length}
                                    rowsPerPage={rowsPerPage}
                                    page={currentPage}
                                    onPageChange={(_e, newPage) => setPage(newPage)}
                                    onRowsPerPageChange={(e) => {
                                        setRowsPerPage(parseInt(e.target.value, 10));
                                        setPage(0);
                                    }}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                )}
            </Paper>
        </>
    );
};

export default PlaybookTable;
